import type { ReactElement } from "react";

import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios, { AxiosError } from "axios";
import { Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Location state from navigation
 */
interface LocationState {
  email?: string;
}

/**
 * EmailVerificationPendingPage Component
 *
 * Shows pending verification status with option to resend.
 */
const EmailVerificationPendingPage = (): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const email = locationState?.email || "";
  const [resending, setResending] = useState<boolean>(false);
  const [cooldown, setCooldown] = useState<number>(0);

  const handleResend = async (): Promise<void> => {
    if (cooldown > 0 || !email) return;

    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      toast.success("Verification email sent!");
      setCooldown(120); // 2 minute cooldown

      // Countdown timer
      const interval = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 429) {
        toast.error("Please wait before requesting another email");
      } else {
        toast.error("Failed to resend verification email");
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background:
          "linear-gradient(to bottom, #FFF8F0 0%, #FFE8D6 50%, #FFD4B8 100%)",
      }}
    >
      <div className="w-full max-w-md">
        {/* Card with explicit light theme styling */}
        <div
          className="text-center animate-slide-up"
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "2rem",
            boxShadow: "0 8px 24px rgba(92, 64, 51, 0.12)",
            border: "2px solid #FFE8D6",
          }}
        >
          <div
            className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full"
            style={{
              background: "linear-gradient(135deg, #FF8C61 0%, #FF9F7D 100%)",
            }}
          >
            <Mail className="w-10 h-10 text-white" />
          </div>

          <h1
            className="text-2xl font-bold mb-2"
            style={{ fontFamily: "Chewy, cursive", color: "#5C4033" }}
          >
            Check Your Email
          </h1>

          <p
            className="mb-2"
            style={{ fontFamily: "Quicksand, sans-serif", color: "#8B6F47" }}
          >
            We sent a verification link to:
          </p>

          <p
            className="font-semibold mb-4"
            style={{ fontFamily: "Quicksand, sans-serif", color: "#5C4033" }}
          >
            {email || "your email"}
          </p>

          <p
            className="text-sm mb-6"
            style={{ fontFamily: "Quicksand, sans-serif", color: "#8B6F47" }}
          >
            Click the link in the email to verify your account and start
            creating stories!
          </p>

          <button
            onClick={handleResend}
            disabled={resending || cooldown > 0 || !email}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-50 hover:scale-105 active:scale-95 disabled:hover:scale-100"
            style={{
              background: "linear-gradient(135deg, #FF8C61 0%, #FF9F7D 100%)",
              color: "#FFF8F0",
              fontFamily: "Quicksand, sans-serif"
            }}
          >
            {resending ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {cooldown > 0
              ? `Resend in ${cooldown}s`
              : resending
                ? "Sending..."
                : "Resend Verification Email"}
          </button>

          <button
            onClick={() => navigate("/login")}
            className="w-full mt-4 text-sm font-semibold transition-all hover:opacity-80"
            style={{ fontFamily: "Quicksand, sans-serif", color: "#8B6F47" }}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPendingPage;
